import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import { useAuth } from '../../components/context/AuthContext';
import { db, storage } from '../../firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import { Container, Button, Row, Col, Alert, Spinner, Form, Image, ListGroup, Badge } from 'react-bootstrap';
import { createLogger } from '../../utils/logger';
import { packLineKey } from '../../utils/inventoryPricing';

// HTTP endpoint for notifications (avoids CORS issues)
const NOTIFICATION_FUNCTION_URL = process.env.REACT_APP_NOTIFICATION_FUNCTION_URL || 
  'https://us-central1-pack-my-cart-dev.cloudfunctions.net/sendOrderStatusNotificationHttp';

const logger = createLogger('EmployeePackOrderPage');

export default function EmployeePackOrderPage() {
  const { transactionId } = useParams();
  const { currentUser, userRole, employeeSupermarketId } = useAuth(); 
  const navigate = useNavigate();

  const [transaction, setTransaction] = useState(null);
  const [packedItems, setPackedItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [productImages, setProductImages] = useState({}); // { [productId]: imageUrl }
  const [productDetails, setProductDetails] = useState({}); // { [productId]: productData }
  const [deliveryConfirmed, setDeliveryConfirmed] = useState(false);
  const [supermarketName, setSupermarketName] = useState('');
  const [customerName, setCustomerName] = useState('');

  // Feature flag for customer notifications
  const enableCustomerNotifications = process.env.REACT_APP_ENABLE_CUSTOMER_NOTIFICATIONS === 'true' || process.env.REACT_APP_ENABLE_CUSTOMER_NOTIFICATIONS === '1';

  // Helper function to determine if an order is a full payment
  const isFullPaymentOrder = useCallback((order) => {
    if (order.paymentType === 'full') {
      return true;
    }
    
    const orderSubtotal = order.orderSubtotal || 0;
    const confirmationFeeAmount = order.confirmationFee || parseFloat(process.env.REACT_APP_CONFIRMATION_FEE) || 500;
    const expectedFullTotal = orderSubtotal + confirmationFeeAmount;
    const amountPaid = parseFloat(order.amountPaid) || parseFloat(order.total) || 0;
    
    if (orderSubtotal > 0 && Math.abs(amountPaid - expectedFullTotal) < 0.01) {
      return true;
    }
    
    return false;
  }, []);

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { path: "/employee/dashboard", label: "Employee Hub" },
    { label: transaction ? `Pack Order: ${transactionId.substring(0,8)}...` : "Pack Order" }
  ];

  const initPackedItemsState = useCallback((cartItems) => {
    const initialPackedState = {};
    (cartItems || []).forEach(item => {
      initialPackedState[packLineKey(item)] = {
        packed: item.packed || false,
        expiry: item.expiryDate || '',
        photoUrl: item.photoUrl || '',
        photoName: item.photoName || '',
        isUploading: false, uploadProgress: 0,
      };
    });
    setPackedItems(initialPackedState);
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setError("Please log in to pack orders."); setLoading(false); return;
    }
    if (userRole !== 'Admin' && userRole !== 'Employee') {
      setError("Access Denied. Requires Admin or Employee role."); setLoading(false); return;
    }
    const transactionRef = doc(db, 'Transactions', transactionId);
    getDoc(transactionRef).then(docSnap => {
      if (docSnap.exists()) {
        const transData = { id: docSnap.id, ...docSnap.data() };
        if (userRole === 'Employee' && transData.supermarketId !== employeeSupermarketId) {
          setError("Access Denied. You are not assigned to this order's supermarket.");
          setTransaction(null);
        } else if (transData.status !== 'payment_successful' && transData.status !== 'awaiting_packing') {
          setError(`This order is not in a state to be packed (Status: ${transData.status}).`);
          setTransaction(transData); 
        } else {
          setTransaction(transData);
          if (transData.packedItemsDetails && transData.packedItemsDetails.length > 0) {
            const prefillPackedState = {};
            transData.packedItemsDetails.forEach(item => {
                prefillPackedState[packLineKey(item)] = {
                    packed: item.packed || false,
                    expiry: item.expiryDate || '',
                    photoUrl: item.photoUrl || '',
                    photoName: item.photoName || '',
                    isUploading: false, uploadProgress: 0,
                };
            });
            setPackedItems(prefillPackedState);
          } else {
            initPackedItemsState(transData.cartItems);
          }
        }
      } else {
        setError("Transaction not found.");
      }
      setLoading(false);
    }).catch(err => {
      setError(`Failed to load transaction details. ${  err.message}`);
      setLoading(false);
    });
  }, [transactionId, currentUser, userRole, employeeSupermarketId, initPackedItemsState]);

  // Function to fetch supermarket name
  const fetchSupermarketName = useCallback(async (supermarketId) => {
    if (!supermarketId) return;
    
    try {
      const supermarketDoc = await getDoc(doc(db, 'Supermarkets', supermarketId));
      if (supermarketDoc.exists()) {
        const supermarketData = supermarketDoc.data();
        setSupermarketName(supermarketData.display_name || supermarketData.name || 'Unknown hardware store');
      } else {
        setSupermarketName('Unknown hardware store');
      }
    } catch (error) {
      logger.error(`Failed to fetch store details for ${supermarketId}:`, error);
      setSupermarketName('Unknown hardware store');
    }
  }, []);

  // Function to fetch customer name
  const fetchCustomerName = useCallback(async (userId) => {
    if (!userId) {
      setCustomerName('Customer');
      return;
    }
    
    try {
      // First try to find user by identity_id field (this is what's stored in transactions)
      const usersQuery = query(
        collection(db, 'Users'),
        where('identity_id', '==', userId),
        limit(1)
      );
      const userSnapshot = await getDocs(usersQuery);
      
      if (!userSnapshot.empty) {
        const userData = userSnapshot.docs[0].data();
        const name = userData.first_name || userData.displayName || userData.email || 'Customer';
        setCustomerName(name);
        logger.debug(`[EmployeePackOrderPage] Found user by identity_id: ${name} for userId: ${userId}`);
      } else {
        // If not found by identity_id, try to find by document ID (in case userId is the document ID)
        logger.debug(`[EmployeePackOrderPage] User not found by identity_id, trying document ID: ${userId}`);
        const userDoc = await getDoc(doc(db, 'Users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const name = userData.first_name || userData.displayName || userData.email || 'Customer';
          setCustomerName(name);
          logger.debug(`[EmployeePackOrderPage] Found user by document ID: ${name} for userId: ${userId}`);
        } else {
          logger.warn(`[EmployeePackOrderPage] User not found for userId: ${userId} (tried both identity_id and document ID)`);
          setCustomerName('Customer');
        }
      }
    } catch (error) {
      logger.error(`[EmployeePackOrderPage] Error fetching user data for userId: ${userId}:`, error);
      setCustomerName('Customer');
    }
  }, []);

  // Effect to fetch supermarket name and customer name when transaction is loaded
  useEffect(() => {
    if (transaction) {
      // Fetch supermarket name
      if (transaction.supermarketId) {
        // If transaction already has supermarketName, use it
        if (transaction.supermarketName && transaction.supermarketName !== 'N/A') {
          setSupermarketName(transaction.supermarketName);
        } else {
          // Otherwise fetch it from the Supermarkets collection
          fetchSupermarketName(transaction.supermarketId);
        }
      }
      
      // Fetch customer name
      if (transaction.userId) {
        // If transaction already has customerName, use it
        if (transaction.customerName && transaction.customerName !== 'Customer' && transaction.customerName !== 'Simulated Customer') {
          setCustomerName(transaction.customerName);
        } else {
          // Otherwise fetch it from the Users collection
          fetchCustomerName(transaction.userId);
        }
      } else {
        setCustomerName('Customer');
      }
    }
  }, [transaction, fetchSupermarketName, fetchCustomerName]);

  // Function to fetch product details and images
  const fetchProductDetails = useCallback(async (productId) => {
    if (productDetails[productId]) return; // Already fetched
    
    try {
      const productDoc = await getDoc(doc(db, 'Products', productId));
      if (productDoc.exists()) {
        const productData = productDoc.data();
        setProductDetails(prev => ({ ...prev, [productId]: productData }));
        
        // Fetch product image if available
        if (productData.media && productData.media.length > 0 && productData.media[0].url) {
          try {
            const imageRef = ref(storage, productData.media[0].url);
            const imageUrl = await getDownloadURL(imageRef);
            setProductImages(prev => ({ ...prev, [productId]: imageUrl }));
          } catch (imageError) {
            logger.warn(`Failed to load image for product ${productId}:`, imageError);
          }
        }
      }
    } catch (error) {
      logger.error(`Failed to fetch product details for ${productId}:`, error);
    }
  }, [productDetails]);

  // Effect to fetch product details when transaction changes
  useEffect(() => {
    if (!transaction || !transaction.cartItems) return;
    
    // Fetch details for all products in the transaction
    transaction.cartItems.forEach(item => {
      if (item.productId) {
        fetchProductDetails(item.productId);
      }
    });
  }, [transaction, fetchProductDetails]);

  const handleItemChange = (itemId, field, value) => {
    setPackedItems(prev => ({ ...prev, [itemId]: { ...prev[itemId], [field]: value }}));
  };

  const handlePhotoUpload = (itemId, file) => {
    if (!file || !transaction) return;
    if (!((userRole === 'Employee' && transaction.supermarketId === employeeSupermarketId) || userRole === 'Admin')) {
        alert("You are not authorized to upload photos for this order."); return;
    }
    
    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      alert(`File ${file.name} is too large. Maximum size is 5MB.`);
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      alert(`File ${file.name} is not an image. Please select image files only.`);
      return;
    }
    
    // Check for problematic filenames
    if (file.name.includes('localhost_3000') || 
        file.name.includes('cart_instance_') || 
        file.name.includes('order-status')) {
      alert(`File ${file.name} has a problematic filename. Please rename the file before uploading.`);
      return;
    }
    
    // Sanitize filename
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const timestamp = Date.now();
    const storagePath = `packing_photos/${transaction.supermarketId || 'unknown_supermarket'}/${transactionId}/${String(itemId).replace(/[^a-zA-Z0-9_-]/g, '_')}/${timestamp}_${sanitizedName}`;
    
                         const fileRef = ref(storage, storagePath);
         const uploadTask = uploadBytesResumable(fileRef, file);
    handleItemChange(itemId, 'isUploading', true);
    handleItemChange(itemId, 'photoName', file.name);
    
    uploadTask.on('state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        handleItemChange(itemId, 'uploadProgress', progress);
        
      },
      (uploadError) => { 
        logger.error('Upload error for file:', file.name, uploadError);
        logger.error('Error details:', {
          code: uploadError.code,
          message: uploadError.message,
          serverResponse: uploadError.serverResponse
        });
        alert(`Upload failed: ${uploadError.message}`); 
        handleItemChange(itemId, 'isUploading', false); 
      },
             () => getDownloadURL(uploadTask.snapshot.ref).then(url => {
        handleItemChange(itemId, 'photoUrl', url);
        handleItemChange(itemId, 'isUploading', false);
        
      }).catch(urlError => {
        logger.error('Error getting download URL for file:', file.name, urlError);
        alert(`Failed to get download URL: ${urlError.message}`);
        handleItemChange(itemId, 'isUploading', false);
      })
    );
  };

  const handleConfirmPackingComplete = async () => {
    if (!transaction || !((userRole === 'Employee' && transaction.supermarketId === employeeSupermarketId) || userRole === 'Admin')) {
        alert("Not authorized or transaction not loaded."); return;
    }
    if (transaction.status !== 'payment_successful' && transaction.status !== 'awaiting_packing') {
        alert("This order is not in a state that can be marked as packed."); return;
    }
    if (!transaction.cartItems.every(item => packedItems[packLineKey(item)]?.packed)) {
        alert("Please ensure all items are marked as packed."); return;
    }
    
    // Check delivery confirmation for delivery orders
    if (transaction.deliveryOption === 'delivery' && !deliveryConfirmed) {
        alert("Please confirm that you understand this order will be marked as 'Out for Delivery' and the customer will be notified.");
        return;
    }
    setIsSubmitting(true);
    const transactionDocRef = doc(db, 'Transactions', transactionId);
    const packedItemsDetailsForDB = transaction.cartItems.map(item => ({
        productId: item.productId,
        productTypeId: item.productTypeId || null,
        name: item.name,
        quantity: item.quantity,
        packed: packedItems[packLineKey(item)]?.packed || false,
        expiryDate: packedItems[packLineKey(item)]?.expiry || null,
        photoUrl: packedItems[packLineKey(item)]?.photoUrl || null,
        photoName: packedItems[packLineKey(item)]?.photoName || null,
    }));
    try {
      // Determine status based on delivery option
      const newStatus = transaction.deliveryOption === 'delivery' ? 'packed_awaiting_pickup' : 'packed_awaiting_pickup';
      
      await updateDoc(transactionDocRef, {
        status: newStatus, 
        packedItemsDetails: packedItemsDetailsForDB,
        packedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        packedBy: currentUser.uid,
      });
      if (transaction.userId && transaction.supermarketId && transaction.cartInstanceId) {
          const userCartRef = doc(db, 'Carts', transaction.userId);
          const cartInstanceUpdate = {};
          const instancePathPrefix = `${transaction.supermarketId}.${transaction.cartInstanceId}`;
          cartInstanceUpdate[`${instancePathPrefix}.status`] = newStatus;
          cartInstanceUpdate[`${instancePathPrefix}.hasUnseenUpdate`] = true; // Mark for notification
          cartInstanceUpdate[`${instancePathPrefix}.lastUpdatedAt`] = firebase.firestore.FieldValue.serverTimestamp();
          cartInstanceUpdate.lastGlobalUpdate = firebase.firestore.FieldValue.serverTimestamp();
          try { await updateDoc(userCartRef, cartInstanceUpdate); } 
          catch (cartError) { logger.warn(`[EmpPackOrder] Failed to update cart instance ${transaction.cartInstanceId} status: ${cartError.message}.`); }
      }

      // Send FCM notification (only if user has FCM token and feature flag is enabled)
      if (enableCustomerNotifications) {
        try {
          // Check if user has FCM token before attempting to send notification
          // First try to find user by identity_id (Firebase Auth UID)
          const usersRef = collection(db, 'Users');
          const userQuery = query(usersRef, where('identity_id', '==', transaction.userId));
          const querySnapshot = await getDocs(userQuery);
          
          let userDoc = null;
          if (!querySnapshot.empty) {
            userDoc = querySnapshot.docs[0];
          } else {
            // Fallback: try direct document lookup
            const directUserDoc = await getDoc(doc(db, 'Users', transaction.userId));
            if (directUserDoc.exists()) {
              userDoc = directUserDoc;
            }
          }
          
          if (userDoc && userDoc.data().fcmToken) {
            // Use HTTP endpoint to avoid CORS issues
            // Don't block on notification - fire and forget to prevent UI delays
            fetch(NOTIFICATION_FUNCTION_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                userId: transaction.userId,
                orderId: transactionId,
                status: newStatus,
                message: `Your order has been packed and is ready for ${transaction.deliveryOption === 'delivery' ? 'delivery' : 'pickup'}.`
              })
            })
            .then(response => {
              if (!response.ok) {
                logger.warn('[EmpPackOrder] Notification failed:', response.status);
              } else {
                return response.json();
              }
            })
            .then(result => {
              if (result && result.success) {
                logger.debug('[EmpPackOrder] FCM notification sent successfully');
              } else if (result) {
                logger.warn('[EmpPackOrder] Notification sent but returned success: false', result);
              }
            })
            .catch(notificationError => {
              logger.warn('[EmpPackOrder] Failed to send FCM notification:', notificationError);
              // Don't fail the packing process if notification fails
            });
            // Continue immediately without waiting for notification
          } else {
            logger.debug('[EmpPackOrder] User has no FCM token, skipping notification');
          }
        } catch (notificationError) {
          logger.warn('[EmpPackOrder] Failed to send FCM notification:', notificationError);
          // Don't fail the packing process if notification fails
        }
      } else {
        logger.debug('[EmpPackOrder] Customer notifications are disabled via feature flag');
      }

      alert("Order packing confirmed!");
      navigate('/employee/dashboard');
    } catch (err) {
      alert(`Failed to confirm packing: ${err.message}.`);
      setIsSubmitting(false);
    }
  };

  if (loading) return (
    <Layout>
      <Container className="mt-4 px-3 text-center">
        <Spinner animation="border" className="mb-3" />
        <p>Loading order details...</p>
      </Container>
    </Layout>
  );
  
  if (error) return (
    <Layout>
      <Container className="mt-4 px-3">
        <Alert variant="danger">{error}</Alert>
        <Button 
          onClick={() => navigate('/employee/dashboard')} 
          className="btn btn-secondary mt-3 w-100"
        >
          Back to Dashboard
        </Button>
      </Container>
    </Layout>
  );
  
  if (!transaction) return (
    <Layout>
      <Container className="mt-4 px-3">
        <Alert variant="info">Could not load transaction data.</Alert>
        <Button 
          onClick={() => navigate('/employee/dashboard')} 
          className="btn btn-secondary mt-3 w-100"
        >
          Back to Dashboard
        </Button>
      </Container>
    </Layout>
  );
  
  const canPackOrder = (transaction.status === 'payment_successful' || transaction.status === 'awaiting_packing') && 
                       (userRole === 'Admin' || (userRole === 'Employee' && transaction.supermarketId === employeeSupermarketId));

  return (
    <Layout>
      <Container className="mt-3 px-3">
        <Row className="align-items-center mb-1">
            <Col xs="auto">
                <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
                    &larr; Go Back
                </Button>
            </Col>
            <Col>
                <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
        </Row>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mt-3 gap-3">
          <div className="flex-grow-1">
            <h2 className="mb-2">
              <span className="d-block d-lg-none text-break">Pack Order: {transaction.id.substring(0,8)}...</span>
              <span className="d-none d-lg-block text-break">Pack Order: {transaction.id}</span>
            </h2>
            <p className="mb-1 text-break">
              <i className="fas fa-user me-1 text-primary"></i>
              <strong>Customer:</strong> {customerName || transaction.customerName || 'Customer'}
            </p>
            <p className="mb-1 text-break">Cart: {transaction.cartInstanceId ? transaction.cartInstanceId.replace('cart_instance_','') : 'N/A'}</p>
            <p className="mb-0 text-break">Hardware store: {supermarketName || transaction.supermarketName || 'N/A'} {userRole === 'Admin' && "(Admin Access)"}</p>
          </div>
        </div>
        
        {/* Full Payment Indicator */}
        {transaction && isFullPaymentOrder(transaction) && (
          <Alert variant="success" className="mb-3">
            <div className="d-flex align-items-center">
              <i className="fas fa-check-circle me-2 fa-lg"></i>
              <div>
                <strong className="d-block mb-1">Full Payment - Paid in Full</strong>
                <small className="text-muted">
                  The customer has paid the full order total. Confirmation fee collected as processing fee. 
                  No amount is due at pickup.
                </small>
              </div>
            </div>
            <div className="mt-2 pt-2 border-top">
              <Row className="g-2">
                <Col xs={12} sm={6} md={4}>
                  <small className="text-muted d-block">Order Subtotal:</small>
                  <strong className="text-success">${Number(transaction.orderSubtotal || 0).toFixed(2)} (Paid)</strong>
                </Col>
                <Col xs={12} sm={6} md={4}>
                  <small className="text-muted d-block">Processing Fee:</small>
                  <strong className="text-success">${Number(transaction.confirmationFee || parseFloat(process.env.REACT_APP_CONFIRMATION_FEE) || 500).toFixed(2)}</strong>
                </Col>
                <Col xs={12} sm={6} md={4}>
                  <small className="text-muted d-block">Total Paid:</small>
                  <strong className="text-success">
                    ${(Number(transaction.orderSubtotal || 0) + Number(transaction.confirmationFee || parseFloat(process.env.REACT_APP_CONFIRMATION_FEE) || 500)).toFixed(2)}
                  </strong>
                </Col>
              </Row>
            </div>
          </Alert>
        )}
        
        {/* Delivery Order Indicator */}
        {transaction.deliveryOption === 'delivery' && (
          <div className="mb-3 p-3 border rounded bg-light">
            <div className="d-flex align-items-center mb-2">
              <span className="badge bg-info text-white me-2">
                <i className="fas fa-truck me-1"></i>
                Delivery Order
              </span>
            </div>
            {transaction.deliveryAddress && (
              <div className="mb-2">
                <small className="text-muted d-block">
                  <strong>Delivery Address:</strong> {transaction.deliveryAddress}
                </small>
              </div>
            )}
            {transaction.deliveryPhone && (
              <div className="mb-0">
                <small className="text-muted d-block">
                  <strong>Delivery Phone:</strong> {transaction.deliveryPhone}
                </small>
              </div>
            )}
          </div>
        )}
        {!canPackOrder && (transaction.status !== 'payment_successful' && transaction.status !== 'awaiting_packing') && <Alert variant="warning">This order is currently in status: {transaction.status}. It cannot be packed now.</Alert>}
        {!canPackOrder && (transaction.status === 'payment_successful' || transaction.status === 'awaiting_packing') && userRole === 'Employee' && transaction.supermarketId !== employeeSupermarketId &&
          <Alert variant="danger">Access Denied: You are not assigned to pack orders for this supermarket.</Alert>}
        <ListGroup className="mb-4">
          {(transaction.cartItems || []).map(item => {
            const lineId = packLineKey(item);
            const productDetail = productDetails[item.productId];
            const productImage = productImages[item.productId];
            
            return (
              <ListGroup.Item key={lineId} className="mb-3 p-3 p-md-3 shadow-sm border-0 bg-white rounded">
                <div className="d-flex flex-column flex-lg-row align-items-start gap-3 gap-lg-3">
                  {/* Product Image */}
                  <div className="text-center text-lg-start mx-auto mx-lg-0" style={{ width: '80px', height: '80px', flexShrink: 0 }}>
                    {productImage ? (
                      <div className="position-relative h-100">
                        <Image 
                          src={productImage} 
                          alt={item.name || 'Product'} 
                          className="img-fluid rounded shadow-sm"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        {packedItems[lineId]?.packed && (
                          <div className="position-absolute top-0 end-0 bg-success text-white rounded-circle d-flex align-items-center justify-content-center" 
                               style={{ width: '20px', height: '20px', fontSize: '10px' }}>
                            <i className="fas fa-check"></i>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div 
                        className="d-flex align-items-center justify-content-center bg-light rounded shadow-sm h-100 border"
                        style={{ 
                          fontSize: '0.8rem',
                          color: '#6c757d'
                        }}
                      >
                        <i className="fas fa-image text-muted"></i>
                                            </div>
                    )}
                  </div>
                  
                  {/* Product Details */}
                  <div className="flex-grow-1 w-100">
                    <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-3">
                      <div className="flex-grow-1">
                        <h5 className="mb-1 text-break fw-bold text-dark">
                          {item.name || productDetail?.display_name || 'Product'}
                        </h5>
                        {productDetail?.description && (
                          <p className="text-muted small mb-2 text-break">
                            {productDetail.description.length > 120 
                              ? `${productDetail.description.substring(0, 120)}...` 
                              : productDetail.description}
                          </p>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-primary fs-6 px-3 py-2">
                          <i className="fas fa-boxes me-1"></i>
                          Qty: {item.quantity}
                        </span>
                        {packedItems[item.productId]?.packed && (
                          <span className="badge bg-success fs-6 px-3 py-2">
                            <i className="fas fa-check me-1"></i>
                            Packed
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Form Controls - Enhanced responsive layout */}
                    <div className="row g-2 g-lg-3">
                      {/* Item Packed Checkbox */}
                      <div className="col-12 col-lg-3">
                        <Form.Group className="d-flex align-items-center p-2 p-lg-3 bg-light rounded border">
                          <Form.Check 
                            type="checkbox" 
                            id={`packed-${lineId}`} 
                            label={
                              <span className="ms-2 fw-semibold small">
                                <i className="fas fa-box me-1"></i>
                                Mark as Packed
                              </span>
                            }
                            checked={packedItems[lineId]?.packed || false}
                            onChange={(e) => handleItemChange(lineId, 'packed', e.target.checked)}
                            disabled={!canPackOrder || isSubmitting}
                            className="mb-0"
                          />
                        </Form.Group>
                      </div>
                      
                      {/* Expiry Date */}
                      <div className="col-12 col-lg-4">
                        <Form.Group>
                          <Form.Label htmlFor={`expiry-${lineId}`} className="small fw-semibold text-muted mb-1">
                            <i className="fas fa-calendar me-1"></i>
                            Expiry Date:
                          </Form.Label>
                          <Form.Control 
                            type="date" 
                            size="sm" 
                            id={`expiry-${lineId}`}
                            value={packedItems[lineId]?.expiry || ''}
                            onChange={(e) => handleItemChange(lineId, 'expiry', e.target.value)}
                            disabled={!canPackOrder || isSubmitting}
                            className="border bg-white shadow-sm w-100"
                            style={{ minHeight: '38px' }}
                          />
                        </Form.Group>
                      </div>
                      
                      {/* Photo Upload */}
                      <div className="col-12 col-lg-5">
                        <Form.Group>
                          <Form.Label htmlFor={`photo-${lineId}`} className="small fw-semibold text-muted mb-1">
                            <i className="fas fa-camera me-1"></i>
                            Photo:
                          </Form.Label>
                          <div className="position-relative">
                            <Form.Control 
                              type="file" 
                              size="sm" 
                              id={`photo-${lineId}`}
                              accept="image/*" 
                              onChange={(e) => handlePhotoUpload(lineId, e.target.files[0])}
                              disabled={!canPackOrder || packedItems[lineId]?.isUploading || isSubmitting}
                              className="border bg-white shadow-sm w-100"
                              style={{ minHeight: '38px' }}
                            />
                            {packedItems[lineId]?.isUploading && (
                              <div className="position-absolute bottom-0 start-0 w-100">
                                <div className="progress" style={{height: '4px'}}>
                                  <div 
                                    className="progress-bar bg-success" 
                                    role="progressbar" 
                                    style={{width: `${packedItems[lineId]?.uploadProgress || 0}%`}}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </Form.Group>
                      </div>
                    </div>
                    
                    {/* Uploaded Photo Display */}
                    {packedItems[lineId]?.photoUrl && (
                      <div className="mt-4 p-4 border rounded bg-light text-center">
                        <div className="position-relative d-inline-block">
                          <Image 
                            src={packedItems[lineId]?.photoUrl} 
                            alt="Packed item" 
                            style={{maxWidth: '100%', maxHeight: '250px', objectFit: 'contain'}} 
                            thumbnail 
                            className="img-fluid shadow-sm"
                          />
                          <div className="position-absolute top-0 end-0 bg-success text-white rounded-circle d-flex align-items-center justify-content-center" 
                               style={{ width: '20px', height: '20px', fontSize: '10px' }}>
                            <i className="fas fa-check"></i>
                          </div>
                        </div>
                        <p className="small text-muted mb-0 mt-3 text-break">
                          <i className="fas fa-image me-1"></i>
                          {packedItems[lineId]?.photoName}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </ListGroup.Item>
             );
           })}
         </ListGroup>
        {/* Delivery Confirmation for Delivery Orders */}
        {canPackOrder && transaction.deliveryOption === 'delivery' && (
          <div className="mt-4 p-4 border rounded bg-light">
            <div className="alert alert-info mb-3">
              <div className="d-flex align-items-start">
                <i className="fas fa-truck me-2 mt-1"></i>
                <div>
                  <strong>Delivery Order Notice:</strong>
                  <br />
                  This is a delivery order. When you confirm packing complete, the order will be marked as "Out for Delivery" 
                  and the customer will be notified that their order is on its way.
                </div>
              </div>
            </div>
            <Form.Check
              type="checkbox"
              id="delivery-confirmation"
              label="I understand that confirming packing complete will mark this order as 'Out for Delivery' and notify the customer"
              checked={deliveryConfirmed}
              onChange={(e) => setDeliveryConfirmed(e.target.checked)}
              className="mb-0"
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-4 mb-5 d-flex flex-column flex-sm-row gap-3">
          {canPackOrder && (
            <Button 
              variant="success" 
              size="lg" 
              className="mx-auto" 
              onClick={handleConfirmPackingComplete}
              disabled={isSubmitting || (transaction.cartItems || []).some(item => packedItems[packLineKey(item)]?.isUploading)}
              style={{
                minWidth: '280px',
                maxWidth: '400px'
              }}
            >
              {isSubmitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" /> 
                  Submitting...
                </>
              ) : (
                <>
                  <i className="fas fa-check-circle me-2"></i>
                  Confirm Packing Complete
                </>
              )}
            </Button>
          )}
          {!canPackOrder && (
            <Button 
              variant="secondary" 
              className="mx-auto"
              onClick={() => navigate('/employee/dashboard')}
              style={{
                minWidth: '280px',
                maxWidth: '400px'
              }}
            >
              Back to Employee Dashboard
            </Button>
          )}
        </div>
      </Container>
    </Layout>
  );
}
